import { useState, useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { ApplicationStatus } from '../applicationProgress';
import { useApplicationVideoGates } from './useApplicationVideoGates';

const STORAGE_KEY = 'applicationVideoAutoPopupDismissed';

const readDismissed = async (isMobile: boolean): Promise<boolean> => {
  try {
    if (isMobile) {
      return (await AsyncStorage.getItem(STORAGE_KEY)) === 'true';
    }
    if (typeof window !== 'undefined') {
      return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
    }
    return false;
  } catch (error) {
    console.warn('useAutoVideoPopup: failed to read dismissed flag', error);
    return false;
  }
};

const persistDismissed = async (isMobile: boolean): Promise<void> => {
  try {
    if (isMobile) {
      await AsyncStorage.setItem(STORAGE_KEY, 'true');
    } else if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(STORAGE_KEY, 'true');
    }
  } catch (error) {
    console.warn('useAutoVideoPopup: failed to persist dismissed flag', error);
  }
};

type UseAutoVideoPopupParams = {
  isMobile?: boolean;
};

export const useAutoVideoPopup = ({ isMobile = false }: UseAutoVideoPopupParams = {}) => {
  const { currentUser } = useCurrentUser();
  const { isProAppVideoEnabled, isAutopopupEnabled } = useApplicationVideoGates();
  const isAccountInfoStep =
    currentUser?.professional?.application_status === ApplicationStatus.AccountInfo;

  const [isOpen, setIsOpen] = useState(false);
  const hasAutoOpened = useRef(false);

  useEffect(() => {
    const maybeAutoOpen = async () => {
      if (
        hasAutoOpened.current ||
        !isProAppVideoEnabled ||
        !isAutopopupEnabled ||
        !isAccountInfoStep
      ) {
        return;
      }

      const dismissed = await readDismissed(isMobile);
      if (dismissed || hasAutoOpened.current) {
        return;
      }

      hasAutoOpened.current = true;
      setIsOpen(true);
      await persistDismissed(isMobile);
    };

    maybeAutoOpen();
  }, [isProAppVideoEnabled, isAutopopupEnabled, isAccountInfoStep, isMobile]);

  const open = () => {
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    persistDismissed(isMobile);
  };

  return { isOpen, open, close };
};
